/**
 * Calendar utility functions for adding events to user calendars
 */

import { stripHtml } from '@/utils/helpers/html';

export interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate?: Date;
  duration?: number; // Duration in hours, defaults to 1 if not provided
}

/**
 * Format date for iCal format (YYYYMMDDTHHmmssZ)
 */
const formatDateForICal = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Escape text for iCal format
 */
const escapeICalText = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
};

/**
 * Generate Google Calendar URL
 */
export const generateGoogleCalendarUrl = (event: CalendarEvent): string => {
  const startDate = event.startDate;
  const endDate = event.endDate || new Date(startDate.getTime() + (event.duration || 1) * 60 * 60 * 1000);
  
  const formatDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
    details: stripHtml(event.description),
    location: event.location,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 */
export const generateOutlookCalendarUrl = (event: CalendarEvent): string => {
  const startDate = event.startDate;
  const endDate = event.endDate || new Date(startDate.getTime() + (event.duration || 1) * 60 * 60 * 1000);
  
  const formatDate = (date: Date): string => {
    return date.toISOString();
  };

  const params = new URLSearchParams({
    subject: event.title,
    startdt: formatDate(startDate),
    enddt: formatDate(endDate),
    body: stripHtml(event.description),
    location: event.location,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate iCal (.ics) file content
 */
export const generateICalContent = (event: CalendarEvent): string => {
  const startDate = event.startDate;
  const endDate = event.endDate || new Date(startDate.getTime() + (event.duration || 1) * 60 * 60 * 1000);
  
  const startDateFormatted = formatDateForICal(startDate);
  const endDateFormatted = formatDateForICal(endDate);
  const now = formatDateForICal(new Date());
  
  const uid = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}@afsa.org`;
  
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AFSA//Event Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${startDateFormatted}`,
    `DTEND:${endDateFormatted}`,
    `SUMMARY:${escapeICalText(event.title)}`,
    `DESCRIPTION:${escapeICalText(stripHtml(event.description))}`,
    `LOCATION:${escapeICalText(event.location)}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
};

/**
 * Download iCal file
 */
export const downloadICalFile = (event: CalendarEvent, filename?: string): void => {
  const content = generateICalContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename || `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Open calendar in new window/tab
 */
export const openCalendar = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

